

import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import Button from '../../../components/base/Button';
import { useNavigate } from 'react-router-dom';

export default function WeightLossPage() {
  const navigate = useNavigate();

  const pricingPlans = [
    {
      name: 'Semaglutide Program',
      price: '$85-$180',
      period: '/month',
      description: 'Our foundational GLP-1 medication program',
      features: [
        'Weekly Semaglutide injections',
        'Medical supervision',
        'Personalized dosing',
        'Monthly check-ins',
        'Nutritional guidance',
        'Progress tracking'
      ],
      popular: false
    },
    {
      name: 'Tirzepatide Premium',
      price: '$150-$265',
      period: '/month',
      description: 'Advanced dual-action weight loss therapy',
      features: [
        'Weekly Tirzepatide injections',
        'Enhanced weight loss results',
        'Comprehensive medical monitoring',
        'Bi-weekly consultations',
        'Advanced nutritional planning',
        'Priority support'
      ],
      popular: true
    },
    {
      name: 'Omega Capsules',
      price: '$100',
      period: '/30-day supply',
      description: 'Boost your weight loss by 33% month over month',
      features: [
        'Prescription-grade Omega capsules',
        '33% enhanced weight loss',
        'Improved metabolism',
        'Better nutrient absorption',
        'Anti-inflammatory benefits',
        'Cognitive enhancement'
      ],
      popular: false
    }
  ];

  const benefits = [
    {
      icon: 'ri-speed-up-line',
      title: 'Rapid Weight Loss',
      description: 'See significant results within the first month with our proven GLP-1 protocols'
    },
    {
      icon: 'ri-emotion-happy-line',
      title: 'Improved Mood',
      description: 'Experience enhanced emotional well-being and reduced stress eating patterns'
    },
    {
      icon: 'ri-flashlight-line',
      title: 'Boosted Energy',
      description: 'Feel more energetic and motivated throughout your weight loss journey'
    },
    {
      icon: 'ri-brain-line',
      title: 'Mental Clarity',
      description: 'Enhanced focus and cognitive function as your body transforms'
    }
  ];

  const guarantees = [
    {
      icon: 'ri-shield-check-line',
      title: 'Results Guarantee',
      description: 'Guaranteed results or your money back - we stand behind our programs'
    },
    {
      icon: 'ri-time-line',
      title: 'Same Day Medications',
      description: 'Get started immediately with same-day medication availability'
    }
  ];

  const handleGetStarted = () => {
    navigate('/consultation');
  };

  const handleBookConsultation = () => {
    navigate('/consultation');
  };

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-[#8A4F8A] to-[#C8A2C8] text-white overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://public.readdy.ai/ai/img_res/ba85c3758b6cc6ef648a4b88e8ffc9e6.jpg)'
          }}
        ></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-5xl font-bold mb-6">
              Transform Your Body with <span className="text-[#F0F0F0]">GLP-1 Medications</span>
            </h1>
            <p className="text-xl mb-8 text-[#F0F0F0]">
              Achieve rapid, sustainable weight loss with our medically supervised GLP-1 programs. 
              Combined with prescription Omega capsules for enhanced results.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" className="bg-white text-[#8A4F8A] hover:bg-[#F0F0F0]" onClick={handleBookConsultation}>
                <i className="ri-calendar-line mr-2"></i>
                Book Free Consultation
              </Button>
              <Button size="lg" variant="secondary" className="border-white text-white hover:bg-white hover:text-[#8A4F8A]">
                <i className="ri-phone-line mr-2"></i>
                Call 202-269-6600
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">
              Why Our <span className="text-[#8A4F8A]">Weight Loss Programs</span> Work
            </h2>
            <p className="text-xl text-[#B0B0B0] max-w-3xl mx-auto">
              Scientifically proven medications combined with personalized care for optimal results
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-[#8A4F8A] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${benefit.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-black mb-3">{benefit.title}</h3>
                <p className="text-[#B0B0B0]">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 bg-gradient-to-b from-[#F0F0F0] to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">
              Choose Your <span className="text-[#8A4F8A]">Weight Loss Program</span>
            </h2>
            <p className="text-xl text-[#B0B0B0] max-w-3xl mx-auto">
              Flexible pricing options to fit your needs and budget
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {pricingPlans.map((plan, index) => (
              <div key={index} className={`bg-white rounded-xl shadow-lg overflow-hidden ${plan.popular ? 'ring-2 ring-[#8A4F8A] transform scale-105' : ''}`}>
                {plan.popular && (
                  <div className="bg-[#8A4F8A] text-white text-center py-2 text-sm font-semibold">
                    Most Popular
                  </div>
                )}
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-black mb-2">{plan.name}</h3>
                  <p className="text-[#B0B0B0] mb-6">{plan.description}</p>
                  
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-[#8A4F8A]">{plan.price}</span>
                    <span className="text-[#B0B0B0]">{plan.period}</span>
                  </div>

                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-black">
                        <i className="ri-check-line text-[#8A4F8A] mr-3"></i>
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <Button className={`w-full ${plan.popular ? 'bg-[#8A4F8A] hover:bg-[#C8A2C8]' : ''}`} onClick={handleGetStarted}>
                    Get Started
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Guarantees Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">
              Our <span className="text-[#8A4F8A]">Guarantees</span>
            </h2>
            <p className="text-xl text-[#B0B0B0] max-w-3xl mx-auto">
              We stand behind our programs with confidence
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {guarantees.map((guarantee, index) => (
              <div key={index} className="bg-gradient-to-br from-[#F0F0F0] to-white rounded-xl p-8 text-center">
                <div className="w-16 h-16 bg-[#8A4F8A] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${guarantee.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-black mb-3">{guarantee.title}</h3>
                <p className="text-[#B0B0B0]">{guarantee.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 bg-gradient-to-b from-[#F0F0F0] to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">
              How Our <span className="text-[#8A4F8A]">Program Works</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '1', title: 'Free Consultation', description: 'Meet with our medical team to discuss your goals and medical history' },
              { step: '2', title: 'Custom Plan', description: 'Receive a personalized treatment plan tailored to your needs' },
              { step: '3', title: 'Start Treatment', description: 'Begin your GLP-1 injections with same-day medication availability' },
              { step: '4', title: 'Track Progress', description: 'Regular check-ins and adjustments to optimize your results' }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-12 h-12 bg-[#8A4F8A] rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold">{item.step}</span>
                </div>
                <h3 className="text-lg font-bold text-black mb-2">{item.title}</h3>
                <p className="text-[#B0B0B0] text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#8A4F8A] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to Transform Your Life?</h2>
          <p className="text-xl mb-8 text-[#F0F0F0]">
            Start your weight loss journey today with our proven GLP-1 programs
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-[#8A4F8A] hover:bg-[#F0F0F0]" onClick={handleBookConsultation}>
              <i className="ri-calendar-line mr-2"></i>
              Book Free Consultation
            </Button>
            <Button size="lg" variant="secondary" className="border-white text-white hover:bg-white hover:text-[#8A4F8A]">
              <i className="ri-phone-line mr-2"></i>
              Call 202-269-6600
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

